import { useEffect, useState } from "react";
import { MapPin, Lightbulb, X, SparklesIcon, BookOpenCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useRef } from "react";
import Markdown from "react-markdown";
import { useDispatch, useSelector } from "react-redux";

import {
	IKeyTermMitigations,
	IMitigation,
	MitigationType,
	processSuggestion,
} from "../../store/playbook-analysis-slice";
import { customDocumentSearch } from "../../utils/addinUtils";
import { usePlaybook } from "../../utils/playbook";
import { RootState } from "../../store/store";

import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { EditRedline } from "./editor/edit-redline";
import { EditRationale } from "./editor/edit-rationale";
import { useUser } from "./auth-provider";

interface IRiskCardProps {
	mitigationIdx: number;
	keyTermIdx: number;
	type: MitigationType;
}

export function MitigationCard({ type, keyTermIdx, mitigationIdx }: IRiskCardProps) {
	const dispatch = useDispatch();

	const [accept, setAccept] = useState<boolean>(false);
	const [reject, setReject] = useState<boolean>(false);

	const { trackChanges } = usePlaybook();

	const { user } = useUser();

	const contentRef = useRef<string>();
	const missingTermRef = useRef<{ value: string; visible: boolean }>({ value: "", visible: false });

	const keyTerm: IKeyTermMitigations = useSelector((state: RootState) => state.analysis[type].risks[keyTermIdx]);
	const mitigation = keyTerm.values[mitigationIdx];

	useEffect(() => {
		Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, () => onSelectionChange());
	}, []);

	async function handleTrackChange(action: boolean, mitigation: IMitigation) {
		// UI flags
		if (action) {
			setAccept(true);
		} else {
			setReject(true);
		}

		let searchResponse = true;

		// Only run document modification when action = accept
		if (action && !mitigation.newClause) {
			searchResponse = await customDocumentSearch({
				action: "accept",
				reference: mitigation.reference,
				comment: mitigation.rationale,
				html_version: mitigation.new_version,
				insertComment: user.insertComments,
			});
		}

		// If document modification fails for accept
		if (!searchResponse && action) {
			toast.error("Something went wrong while updating the document");
			setAccept(false);
			setReject(false);
			return;
		}

		// Add entry to track changes for both accept/reject
		const response = await trackChanges(action, mitigation.risk_id);

		if (!response.success) {
			toast.error(response.message ?? "Failed to save the redline change");
			setAccept(false);
			setReject(false);
			return;
		}

		// Update reducer state
		dispatch(
			processSuggestion({
				type,
				keyTermIdx,
				mitigationIdx,
				accept: action,
			})
		);

		toast.success(`Mitigation ${action ? "accepted" : "rejected"} successfully`);

		setAccept(false);
		setReject(false);
	}

	async function onSelectionChange() {
		await Word.run(async (context) => {
			if (missingTermRef.current.visible) {
				const selection = context.document.getSelection();
				selection.load("text");
				await context.sync();

				context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

				const newPara = selection.insertText(missingTermRef.current.value, "After");
				// newPara.font.bold = true;
				newPara.insertBreak("Line", "After");
				await context.sync();

				// const newPara = heading.insertText(" " + keyTerm.values.at(0).standard_text, "After");

				context.document.changeTrackingMode = Word.ChangeTrackingMode.off;

				newPara.select();
				await context.sync();
				handleTrackChange(true, mitigation);

				missingTermRef.current.visible = false;
			}
		});
	}

	console.log(mitigation);

	return (
		<Card className="mt-3 rounded-xl py-4">
			<CardContent className="flex gap-3 flex-col px-4">
				{/* {mitigation.comparison_type === MitigationType.CONTRADICTORY && ( */}
				<div className="flex justify-between items-center">
					<div className="flex gap-2">
						<Tooltip>
							<TooltipTrigger>
								<Button
									variant="ghost"
									className="size-8 bg-[#f5f5f5] hover:bg-[#e6e6e6] rounded-full grid items-center justify-center"
									onClick={() => customDocumentSearch({ action: "locate", reference: mitigation.reference })}
								>
									<MapPin className="size-4 text-primary" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								{mitigation.comparison_type === MitigationType.CONTRADICTORY
									? "Tap to locate the issue"
									: "Locate suggested place to insert missing clause"}
							</TooltipContent>
						</Tooltip>

						<Dialog>
							<Tooltip>
								<TooltipTrigger>
									<DialogTrigger>
										<Button
											variant="ghost"
											className="size-8 bg-[#f5f5f5] hover:bg-[#e6e6e6] rounded-full grid items-center justify-center"
										>
											<Lightbulb className="size-4 text-primary" />
										</Button>
									</DialogTrigger>
								</TooltipTrigger>
								<TooltipContent className="max-w-sm">Rationale</TooltipContent>
							</Tooltip>
							<DialogContent className="max-w-[80vw] max-h-[80vh] overflow-auto">
								<DialogHeader className="flex-row items-center gap-4">
									<DialogTitle>Rationale</DialogTitle>
									<EditRationale
										comment={mitigation.rationale}
										type={type}
										mitigation={mitigation}
										keyTermIdx={keyTermIdx}
										mitigationIdx={mitigationIdx}
									/>
								</DialogHeader>
								<div className="text-sm">{mitigation.rationale}</div>
								<DialogHeader className="flex-row items-center gap-4">
									<DialogTitle>Preferred Position as per Playbook</DialogTitle>
									{/* <EditRationale
										comment={mitigation.standard_text.join("\n")}
										type={type}
										mitigation={mitigation}
										keyTermIdx={keyTermIdx}
										mitigationIdx={mitigationIdx}
									/> */}
								</DialogHeader>
								<div className="text-sm">
									{mitigation.standard_text.map((clause, idx) => (
										<div key={idx} className="mb-3 whitespace-pre-wrap flex flex-col gap-2">
											{mitigation.standard_text.length > 1 && (
												<div className="font-semibold">
													Clause {idx + 1} <br />
												</div>
											)}
											<div>{clause}</div>
										</div>
									))}
								</div>
							</DialogContent>
						</Dialog>

						{/* <Dialog>
							<Tooltip>
								<TooltipTrigger>
									<DialogTrigger>
										<Button
											variant="ghost"
											className="size-8 bg-[#f5f5f5] hover:bg-[#e6e6e6] rounded-full grid items-center justify-center"
										>
											<BookOpenCheck className="size-4 text-primary" />
										</Button>
									</DialogTrigger>
								</TooltipTrigger>
								<TooltipContent className="max-w-sm">Standard Text</TooltipContent>
							</Tooltip>
							<DialogContent className="max-w-[80vw] max-h-[80vh] overflow-scroll">
								<DialogHeader className="flex-row items-center gap-4">
									<DialogTitle>Standard Text</DialogTitle>
								</DialogHeader>
								<div className="text-sm">{mitigation.standard_text.join("\n")}</div>
							</DialogContent>
						</Dialog> */}
					</div>

					<div className="flex items-center gap-1">
						<EditRedline type={type} mitigation={mitigation} keyTermIdx={keyTermIdx} mitigationIdx={mitigationIdx} />
						{mitigation.comparison_type === MitigationType.CONTRADICTORY ? (
							<div className="rounded-lg px-3 py-0.5 grid w-fit justify-center items-center text-xs bg-red-50 text-red-500">
								Risks
							</div>
						) : (
							<div className="rounded-lg px-3 py-0.5 grid w-fit justify-center items-center text-xs bg-orange-50 text-orange-500">
								Missing
							</div>
						)}
					</div>
				</div>
				{/* )} */}

				<p
					className="text-xs font-normal"
					ref={(e) => {
						if (e) {
							contentRef.current = e.innerHTML;
						}
					}}
				>
					{mitigation.diff &&
						mitigation.diff.map((text, diffIdx: number) => (
							<span
								key={diffIdx}
								style={{
									textDecoration: text.removed ? "line-through" : "none",
									color: text.removed ? "#dc2626" : text.added ? "#16a34a" : "black",
									fontSize: "0.75rem",
									whiteSpace: "pre-wrap",
								}}
							>
								{text.value}{" "}
							</span>
						))}
				</p>

				<div className="flex justify-between items-center">
					<div className="flex items-center gap-2">
						<Button
							variant="outline"
							disabled={accept || reject}
							className="border-primary text-primary hover:text-primary cursor-pointer min-w-24"
							onClick={async () => {
								if (mitigation.newClause) {
									const addedText = mitigation.diff
										?.filter((item) => item.added)
										.map((item) => item.value)
										.join(" ");
									missingTermRef.current = { visible: true, value: addedText };
									toast.info("Click anywhere in the document to insert the selected clause");
								} else {
									await handleTrackChange(true, mitigation);
								}
							}}
						>
							{accept ? (
								<div className="flex items-center justify-center">
									<Loader2 className="size-4 animate-spin" />
								</div>
							) : (
								"Accept"
							)}
						</Button>
						<Button
							variant="ghost"
							disabled={accept || reject}
							className="bg-[#f5f5f5] hover:bg-[#e6e6e6] cursor-pointer"
							onClick={async () => {
								await handleTrackChange(false, mitigation);
							}}
						>
							{reject ? (
								<div className="flex items-center justify-center">
									<Loader2 className="size-4 animate-spin" />
								</div>
							) : (
								<X className="text-primary size-4" />
							)}
						</Button>
					</div>

					<div>
						<Dialog>
							<DialogTrigger asChild>
								<Button
									variant="ghost"
									className="bg-[#f5f5f5] hover:bg-[#e6e6e6] cursor-pointer text-primary hover:text-primary"
								>
									<span className="flex items-center gap-1">
										AI Reasoning
										<SparklesIcon className="size-4" />
									</span>
								</Button>
							</DialogTrigger>
							<DialogContent className="max-w-[80vw] max-h-[80vh] overflow-auto">
								<DialogHeader>
									<DialogTitle>AI Reasoning</DialogTitle>
								</DialogHeader>

								<div className="text-sm whitespace-pre-wrap wrap-break-word">
									<Markdown>
										{"## Reasoning\n" +
											mitigation.reasoning +
											"\n" +
											"## Consequence\n" +
											mitigation.consequence +
											"\n" +
											"## Solution\n" +
											mitigation.solution}
									</Markdown>
								</div>
							</DialogContent>
						</Dialog>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
